'use client';

// The one connection-health bar, shared verbatim by admin-web and bidder-web
// (BL-1090). Replaces bidder's removed StaleConnectionBanner (BUG-1126) and
// admin's per-surface ConnectionHealthBanner.
//
// Two deliberate design decisions worth keeping:
//
//   1. NO ACTION. Recovery is automatic — the reachability probe re-checks on
//      a widening backoff, and the realtime plane self-heals via the ADR-011
//      §5 reconcile chain (focus / visibility / online / transport-reconnect /
//      periodic tick). A retry control would tell the user they must act when
//      they need not, and admin's outgoing "Resync" was literally inert on the
//      clerk console (it bumped a key nothing read).
//
//   2. NEUTRAL, NOT DIALECT. Admin (settings-v2 `--text-*`) and bidder
//      (`--bw-*`) are intentionally different design dialects per ADR 030 § 2.
//      This bar is system chrome that must read as correct in both, so it uses
//      the neutral zinc ramp and no brand token from either side.

import { WifiOff } from 'lucide-react';
import clsx from 'clsx';

export interface ConnectionHealthBarProps {
    /** Probe-corroborated: the network is genuinely unreachable. */
    isOffline: boolean;
    /** The realtime channel dropped after having joined. */
    isFeedDisconnected: boolean;
    /** Extra classes on the bar root (e.g. a layout override). */
    className?: string;
}

/**
 * Full-width informational bar shown only while the connection is degraded.
 *
 * It is an **in-flow** block (not `position: fixed`), so when it appears it
 * PUSHES the header and everything below it down rather than painting over
 * them, and when the connection is healthy it renders `null` and occupies no
 * layout at all. Each app mounts it as the first child of its top-level shell
 * so "pushes everything down" lands app-wide.
 *
 * @param props - {@link ConnectionHealthBarProps}
 * @returns The bar element, or `null` when the connection is healthy.
 */
export function ConnectionHealthBar({
    isOffline,
    isFeedDisconnected,
    className,
}: ConnectionHealthBarProps) {
    if (!isOffline && !isFeedDisconnected) return null;

    // No network is the more severe and more actionable fact, and it implies
    // the feed is down too — so it wins the copy race. Only the offline state
    // carries an icon; the softer "reconnecting" notice reads cleaner as plain
    // centered text.
    const message = isOffline
        ? "There's a problem with your connection. Live updates will resume automatically when your connection is restored."
        : 'Reconnecting — live updates may be delayed.';

    return (
        <div
            role="status"
            // Polite, not assertive: this can appear while a buyer is typing a
            // bid, and an assertive live region interrupts a screen-reader
            // user mid-action for what is ambient status.
            aria-live="polite"
            data-testid="connection-health-bar"
            className={clsx(
                'flex w-full items-center justify-center gap-2',
                'px-4 py-2 text-center text-sm font-medium',
                'bg-zinc-900 text-white',
                className,
            )}
        >
            {isOffline ? (
                <WifiOff className="h-4 w-4 flex-shrink-0" aria-hidden />
            ) : null}
            <span>{message}</span>
        </div>
    );
}

export default ConnectionHealthBar;
